'use client'

import { useGlobalMessage } from '@/app/_lib/hooks'
import { useReCaptcha } from 'next-recaptcha-v3'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import RecaptchaDisclaimer from '@/app/_components/RecaptchaDisclaimer'
import FormError from '@/app/_components/FormError'
import submitSalesAssistanceAction from '@/app/_lib/actions/SubmitSalesAssistanceAction'

const salesAssistanceSchema = z.object({
    name: z.string().min(1, 'Name is required'),
    email: z.string().min(1, 'Email is required').email('Please enter a valid email'),
    phone: z.string().optional(),
    company: z.string().optional(),
    message: z.string().min(1, 'Message is required'),
})

const SalesAssistanceForm = () => {
    const addGlobalMessage = useGlobalMessage()
    const { executeRecaptcha } = useReCaptcha()

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors, isSubmitting }
    } = useForm({
        resolver: zodResolver(salesAssistanceSchema),
        defaultValues: {
            name: '',
            email: '',
            phone: '',
            company: '',
            message: '',
        }
    })

    const onSubmit = async (data) => {
        try {
            // Get reCAPTCHA token
            const token = await executeRecaptcha('sales_assistance')

            const response = await submitSalesAssistanceAction({
                ...data,
                recaptcha_token: token
            })

            if (response.success) {
                addGlobalMessage({
                    message: 'Thank you for contacting us! We will respond to your inquiry shortly.',
                    messageType: 'success'
                })
                reset()
            } else {
                addGlobalMessage({
                    message: response.message || 'An error occurred. Please try again.',
                    messageType: 'error'
                })
            }
        } catch (error) {
            addGlobalMessage({
                message: 'An error occurred. Please try again.',
                messageType: 'error'
            })
        }
    }

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="max-w-3xl">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="form-group">
                    <input
                        id="sales-name"
                        type="text"
                        autoComplete="name"
                        className={`form-input peer ${errors.name ? 'border-red' : ''}`}
                        placeholder=" "
                        disabled={isSubmitting}
                        aria-invalid={!!errors.name}
                        aria-describedby={errors.name ? "sales-name-error" : undefined}
                        {...register('name')}
                    />
                    <label htmlFor="sales-name" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">
                        Name <span className="text-primary">*</span>
                    </label>
                    {errors.name && (
                        <FormError id="sales-name-error">{errors.name.message}</FormError>
                    )}
                </div>

                <div className="form-group">
                    <input
                        id="sales-email"
                        type="email"
                        autoComplete="email"
                        className={`form-input peer ${errors.email ? 'border-red' : ''}`}
                        placeholder=" "
                        disabled={isSubmitting}
                        aria-invalid={!!errors.email}
                        aria-describedby={errors.email ? "sales-email-error" : undefined}
                        {...register('email')}
                    />
                    <label htmlFor="sales-email" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">
                        Email <span className="text-primary">*</span>
                    </label>
                    {errors.email && (
                        <FormError id="sales-email-error">{errors.email.message}</FormError>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="form-group">
                    <input
                        id="sales-phone"
                        type="tel"
                        autoComplete="tel"
                        inputMode="tel"
                        className="form-input peer"
                        placeholder=" "
                        disabled={isSubmitting}
                        {...register('phone')}
                    />
                    <label htmlFor="sales-phone" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">
                        Phone
                    </label>
                </div>

                <div className="form-group">
                    <input
                        id="sales-company"
                        type="text"
                        autoComplete="organization"
                        className="form-input peer"
                        placeholder=" "
                        disabled={isSubmitting}
                        {...register('company')}
                    />
                    <label htmlFor="sales-company" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">
                        Company
                    </label>
                </div>
            </div>

            <div className="form-group mb-4">
                <textarea
                    id="sales-message"
                    className={`form-input peer ${errors.message ? 'border-red' : ''}`}
                    placeholder=" "
                    rows="6"
                    disabled={isSubmitting}
                    aria-invalid={!!errors.message}
                    aria-describedby={errors.message ? "sales-message-error" : undefined}
                    {...register('message')}
                />
                <label htmlFor="sales-message" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">
                    Message <span className="text-primary">*</span>
                </label>
                {errors.message && (
                    <FormError id="sales-message-error">{errors.message.message}</FormError>
                )}
            </div>

            <div className="mb-4">
                <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`btn btn-primary ${isSubmitting ? 'opacity-50' : ''}`}
                >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
            </div>

            <RecaptchaDisclaimer />
        </form>
    )
}

export default SalesAssistanceForm
